// src/pages/driver-management/DriverScoreSettingPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import DataTable from '../../components/DataTable';
import ActionMenu from '../../components/ActionMenu';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import { useNotifier } from '../../components/Notifier';
import { useLanguage } from '../../utils/i18n';
import { useCompanyList } from '../../stores/companyStore';
import { useDriverScoreConfigList, deleteDriverScoreConfig } from '../../stores/driverScoreConfigStore';
import AddScoreConfigModal from './AddScoreConfigModal';

export default function DriverScoreSettingPage() {
  const { t } = useLanguage();
  const notify = useNotifier();

  // Pagination & Search
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchRaw, setSearchRaw] = useState('');

  // Company Filter
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored && stored !== 'all' ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const match = companyOptionsNormalized.find((o) => String(o.value) === String(companyId));
      if (match) {
        setSelectedCompanies([{ value: String(match.value), label: match.label }]);
      }
    } catch (e) {
      console.error('auto select company failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyIdsStr = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  // Data fetching hook (no category filtering)
  const {
    data: scoreConfigs,
    totalRecord,
    isLoading,
    revalidate
  } = useDriverScoreConfigList(null, paginationModel.page, paginationModel.pageSize, 'name', searchRaw, companyIdsStr);

  // Modal (Add/Edit) state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);

  // Delete Confirm Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingName, setDeletingName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Handlers
  const handlePageChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = ({ value }) => {
    setSearchRaw(value ?? '');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCompanyChange = (newCompanies) => {
    setSelectedCompanies(newCompanies);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setEditingData(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    setEditingId(row?.id ?? null);
    setEditingData(row ?? null);
    setModalOpen(true);
  };

  const handleDeletePrompt = (id, row) => {
    setDeletingId(id);
    setDeletingName(row?.name || `ID ${id}`);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setDeleteLoading(true);
    try {
      await deleteDriverScoreConfig(deletingId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      setDeletingId(null);
      setDeletingName('');
      revalidate();
    } catch (err) {
      console.error('Failed to delete score config:', err);
      const errMsg = err?.response?.data?.message || err?.message || t('notify.deleteFailed', 'Failed to delete');
      notify({ message: errMsg, severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleModalSuccess = () => {
    revalidate();
  };

  // Table Columns
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('general.name', 'Name'),
        flex: 1.2,
        minWidth: 180,
        valueGetter: (value, row) => row?.name || '-'
      },
      {
        field: 'company_name',
        headerName: t('general.company', 'Company'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => row?.company_name || row?.join?.company_name || '-'
      },
      {
        field: 'min_dist_km',
        headerName: `${t('driverScore.minDistance', 'Minimum Distance')} (km)`,
        flex: 0.8,
        minWidth: 180,
        headerAlign: 'right',
        align: 'right',
        valueGetter: (value, row) => (row?.min_dist_km != null ? `${row.min_dist_km} km` : '-')
      },
      {
        field: 'pct_warn_limit',
        headerName: `${t('driverScore.warningThreshold', 'Warning Threshold')} (%)`,
        flex: 0.8,
        minWidth: 190,
        headerAlign: 'right',
        align: 'right',
        valueGetter: (value, row) => (row?.pct_warn_limit != null ? `${Number(row.pct_warn_limit).toFixed(2)}%` : '-')
      },
      {
        field: 'actions',
        headerName: t('general.action', 'Action'),
        width: 100,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <ActionMenu
            mode="inline"
            row={params.row}
            onEdit={() => handleEdit(params.row)}
            onDelete={() => handleDeletePrompt(params.row.id, params.row)}
            disableView
          />
        )
      }
    ],
    [t]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', gap: 2 }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{t('driverScore.pageTitle', 'Driver Score Setting')}</Typography>
      </Box>

      {/* Main DataTable */}
      <DataTable
        title={t('driverScore.configList', 'Driver Score Configuration List')}
        rows={scoreConfigs || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecord || 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ value }) => handleSearch({ value })}
        onAddClick={handleOpenAdd}
        searchLabel={t('driverScore.searchByName', 'Search by name...')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {/* Add / Edit Modal */}
      <AddScoreConfigModal
        open={modalOpen}
        id={editingId}
        initialData={editingData}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setEditingData(null);
        }}
        onSuccess={handleModalSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        loading={deleteLoading}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message={`${t('general.deleteConfirmation', 'Are you sure you want to delete')} "${deletingName}"?`}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingId(null);
          setDeletingName('');
        }}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
